import React, { useState } from 'react';
import { showToast } from '../common/Toaster';
import { Check, ArrowRight, FileDown, PenLine, ShieldCheck, AlertTriangle } from 'lucide-react';
import planApi, { PlanRole } from '../../services/planApi';
import { POLICY_SECTIONS, questionsFor, PolicySection } from './agreementContent';
import AgreementModal from './AgreementModal';

interface Props {
  relId: number;
  role: PlanRole;
  consent: any;
  counterpart: string;
  userName: string;
  onChanged: () => void;
}

const fmtDate = (s?: string) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  return isNaN(d.getTime()) ? s : d.toLocaleString('es', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PolicyBlockView: React.FC<{ section: PolicySection }> = ({ section }) => (
  <div className="ac-pol-section">
    <div className="ac-pol-head">
      <span className="ac-pol-num">{section.num}</span>
      <div className="ac-pol-body">
        <div className="ac-pol-title">{section.title}</div>
        {section.blocks.map((b, i) => {
          if (b.type === 'text') return <p className="ac-pol-text" key={i}>{b.content}</p>;
          if (b.type === 'emphasis') return <p className="ac-pol-emphasis" key={i}>{b.content}</p>;
          if (b.type === 'small') return <p className="ac-pol-small" key={i}>{b.content}</p>;
          if (b.type === 'list') return (
            <div className="ac-pol-list-wrap" key={i}>
              {b.label && <div className="ac-pol-list-label">{b.label}</div>}
              <ul className="ac-pol-list">{(b.items || []).map((t, j) => <li key={j}>{t}</li>)}</ul>
            </div>
          );
          return null;
        })}
      </div>
    </div>
  </div>
);

const ConsentStep: React.FC<Props> = ({ relId, role, consent, counterpart, userName, onChanged }) => {
  const questions = questionsFor(role);
  const menteeSigned = !!consent?.mentee_signed;
  const mentorSigned = !!consent?.mentor_signed;
  const mySigned = role === 'mentee' ? menteeSigned : role === 'mentor' ? mentorSigned : true;
  const myAnswers: Record<string, string> = (role === 'mentor' ? consent?.mentor_answers : consent?.mentee_answers) || {};
  const mySignedAt = role === 'mentor' ? consent?.mentor_accepted_at : consent?.mentee_accepted_at;

  const [phase, setPhase] = useState<'reading' | 'committing'>('reading');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [viewParty, setViewParty] = useState<{ role: 'mentee' | 'mentor'; answers: Record<string, string> | null } | null>(null);

  // Chip de una parte: si es la CONTRAPARTE y ya firmó, es un botón para leer su acuerdo.
  const renderParty = (partyRole: 'mentee' | 'mentor') => {
    const signed = partyRole === 'mentee' ? menteeSigned : mentorSigned;
    const label = partyRole === 'mentee' ? 'Mentee' : 'Mentor';
    const isCounterpart = (role === 'mentee' || role === 'mentor') && partyRole !== role;
    const partyAnswers = partyRole === 'mentee' ? consent?.mentee_answers : consent?.mentor_answers;
    if (isCounterpart && signed) {
      return (
        <button className="ac-party ok ac-party--link" onClick={() => setViewParty({ role: partyRole, answers: partyAnswers || null })}>
          <Check size={14} /> Ver acuerdo de {label.toLowerCase()}
        </button>
      );
    }
    return (
      <span className={`ac-party ${signed ? 'ok' : ''}`}>
        {signed ? <Check size={14} /> : null} {label}: {signed ? 'firmado' : 'pendiente'}
      </span>
    );
  };

  const filled = questions.filter(q => (answers[q.id] || '').trim()).length;
  const allFilled = filled === questions.length;

  const sign = async () => {
    if (!allFilled || saving) return;
    setSaving(true);
    try {
      await planApi.signAgreement(relId, answers);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onChanged();
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'No se pudo firmar el acuerdo', 'error');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 56; const CW = W - M * 2;
    const NAVY: [number, number, number] = [15, 27, 45];
    const RED: [number, number, number] = [200, 16, 46];
    const GOLD: [number, number, number] = [201, 162, 76];
    const GREY: [number, number, number] = [120, 120, 130];
    let y = 0;
    // Cabecera BLANCA con el logo negro de Mitsui (mismo formato que el email)
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, W, 84, 'F');
    try {
      const logoData = await fetch('/img/mitsui_logo_email.png')
        .then(r => r.blob())
        .then(b => new Promise<string>(res => { const fr = new FileReader(); fr.onload = () => res(fr.result as string); fr.readAsDataURL(b); }));
      doc.addImage(logoData, 'PNG', M, 26, 150, 150 * 134 / 460);
    } catch {
      doc.setFont('times', 'bold'); doc.setFontSize(20); doc.setTextColor(...NAVY);
      doc.text('MITSUI & CO.', M, 52);
    }
    doc.setFont('courier', 'normal'); doc.setFontSize(8); doc.setTextColor(150, 150, 160);
    doc.text(`PROGRAMA DE MENTORÍA · ${new Date().getFullYear()}`, W - M, 34, { align: 'right' });
    doc.text(`FIRMADO ${fmtDate(mySignedAt)}`, W - M, 48, { align: 'right' });
    doc.setFillColor(16, 16, 16); doc.rect(0, 84, W, 2.5, 'F');
    y = 152;
    doc.setFont('times', 'normal'); doc.setFontSize(34); doc.setTextColor(...NAVY);
    doc.text('El ', M, y); const ew = doc.getTextWidth('El ');
    doc.setFont('times', 'italic'); doc.setTextColor(...RED); doc.text('Acuerdo', M + ew, y);
    y += 26;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.setTextColor(...NAVY);
    doc.text(`${userName}  ·  ${role === 'mentor' ? 'mentor de' : 'mentee de'} ${counterpart}`, M, y);
    y += 30;
    const ensure = (h: number) => { if (y + h > H - M) { doc.addPage(); y = M; } };
    questions.forEach((q, i) => {
      ensure(60);
      doc.setFont('courier', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...RED);
      doc.text(`0${i + 1}`, M, y); y += 15;
      doc.setFont('times', 'bold'); doc.setFontSize(12); doc.setTextColor(...NAVY);
      doc.splitTextToSize(q.text, CW).forEach((ln: string) => { ensure(18); doc.text(ln, M, y); y += 17; });
      y += 4;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(40, 40, 55);
      doc.splitTextToSize(myAnswers[q.id] || '—', CW).forEach((ln: string) => { ensure(16); doc.text(ln, M, y); y += 15; });
      y += 16;
    });
    ensure(40);
    doc.setDrawColor(...GOLD); doc.line(M, y, W - M, y); y += 18;
    doc.setFont('courier', 'normal'); doc.setFontSize(8); doc.setTextColor(...GREY);
    doc.text('Confidencial', M, y);
    doc.save(`Acuerdo_Mitsui_${userName.replace(/\s+/g, '_')}.pdf`);
  };

  // ---- Estado FIRMADO: resumen + PDF ----
  if (mySigned) {
    return (
      <div className="plan-card ac">
        <div className="ac-signed-banner">
          <ShieldCheck size={18} /> Acuerdo firmado {mySignedAt ? `el ${fmtDate(mySignedAt)}` : ''}
        </div>
        <h2 className="ac-title">Tu compromiso</h2>
        <div className="ac-parties">
          {renderParty('mentee')}
          {renderParty('mentor')}
        </div>
        <div className="ac-commit-list">
          {questions.map((q, i) => (
            <div className="ac-answer" key={q.id}>
              <div className="ac-answer-q"><span className="ac-answer-num">0{i + 1}</span> {q.text}</div>
              <div className="ac-answer-a">{myAnswers[q.id] || '—'}</div>
            </div>
          ))}
        </div>
        <button className="btn-ink" onClick={downloadPdf}><FileDown size={15} /> Descargar acuerdo (PDF)</button>
        {viewParty && (
          <AgreementModal role={viewParty.role} answers={viewParty.answers} name={counterpart} onClose={() => setViewParty(null)} />
        )}
      </div>
    );
  }

  // ---- Fase 1: lectura de la política ----
  if (phase === 'reading') {
    return (
      <div className="plan-card ac">
        <div className="ac-eyebrow"><PenLine size={13} /> Acuerdo · por firmar</div>
        <h2 className="ac-title">Acuerdo de Mentoría</h2>
        <p className="ac-lead">Al final firmarás tu compromiso personal respondiendo unas preguntas.</p>
        <div className="ac-policy">
          {POLICY_SECTIONS.map(s => <PolicyBlockView key={s.id} section={s} />)}
        </div>
        <button className="btn-ink" onClick={() => { setPhase('committing'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          Avanzar si estás de acuerdo <ArrowRight size={15} />
        </button>
      </div>
    );
  }

  // ---- Fase 2: preguntas reflexivas ----
  return (
    <div className="plan-card ac">
      <div className="ac-eyebrow"><PenLine size={13} /> Acuerdo · por firmar</div>
      <h2 className="ac-title">Tu compromiso</h2>
      <p className="ac-lead">No hay respuestas correctas, sólo respuestas tuyas.</p>

      <div className="ac-commit-list">
        {questions.map((q, i) => (
          <div className="ac-commit" key={q.id}>
            <div className="ac-commit-head">
              <span className="ac-commit-num">0{i + 1}</span>
              <div className="ac-commit-q">{q.text}<span className="ac-commit-helper">{q.helper}</span></div>
            </div>
            <textarea
              className="ac-textarea"
              placeholder="Tu respuesta…"
              value={answers[q.id] || ''}
              onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      <div className="ac-sign-bar">
        <span className="ac-progress">{allFilled ? <><Check size={14} /> Listo para firmar</> : <><AlertTriangle size={14} /> {filled}/{questions.length} respondidas</>}</span>
        <button className="btn-ink" disabled={!allFilled || saving} onClick={sign}>
          <PenLine size={15} /> {saving ? 'Firmando…' : 'Firmar acuerdo'}
        </button>
      </div>
      <button className="ac-back" onClick={() => setPhase('reading')}>← Volver a los términos</button>
    </div>
  );
};

export default ConsentStep;
